import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { IdNameDto, ParkingSubscriptionListRow } from '../../../../../core/models/finance/master-fee.model';
import { MapperMasterFeeService } from '../../../../../core/mappers/finance/mapper-master-fee.service';
import { ApiMasterFeeService } from '../../../../../core/services/api/finance/api-master-fee.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';

@Component({
  selector: 'app-modal-membership-package',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal-membership-package.component.html',
})
export class ModalMembershipPackageComponent implements OnInit {
  @Input() editSubscriptionId: number | null = null;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  formData!: ParkingSubscriptionListRow;
  fees: IdNameDto[] = [];
  rates: IdNameDto[] = [];
  gateGroups: IdNameDto[] = [];
  isSaving = signal(false);
  isLoadingDetail = signal(false);

  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiMasterFeeService);
  private readonly mapper = inject(MapperMasterFeeService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean {
    return (this.editSubscriptionId ?? 0) > 0;
  }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptySubscription(this.siteId);
    this.formData.siteId = this.siteId;
    this.formData.isOnline = true;
    this.loadOptions();

    if (this.isEdit && this.editSubscriptionId) {
      this.loadSubscriptionDetail(this.editSubscriptionId);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    if (!this.siteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar');
      return;
    }
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อแพ็กเกจ');
      return;
    }

    const isEdit = this.isEdit;
    if (isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload = this.mapper.mapSubscriptionPayload(this.formData, userId, menuId, this.siteId);
    const request$ = isEdit
      ? this.api.updateMembershipPackage(this.formData.id, payload)
      : this.api.insertMembershipPackage(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadSubscriptionDetail(subscriptionId: number): void {
    this.isLoadingDetail.set(true);
    this.api.getParkingSubscriptionForTable(this.siteId, 1, 200).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapSubscriptionTable(res);
        const found = mapped.items.find((x) => x.id === subscriptionId);
        if (found) {
          this.formData = { ...found };
        } else {
          this.toast.error('ไม่พบข้อมูลแพ็กเกจสำหรับแก้ไข');
        }
        this.isLoadingDetail.set(false);
      },
      error: () => {
        this.isLoadingDetail.set(false);
        this.toast.error('โหลดรายละเอียดแพ็กเกจล้มเหลว');
      },
    });
  }

  private loadOptions(): void {
    this.api.getParkingFeeDropdown().subscribe({
      next: (res) => (this.fees = this.mapper.mapIdNameList(res)),
      error: () => (this.fees = []),
    });

    if (!this.siteId) return;

    this.api.getParkingRateBySite(this.siteId).subscribe({
      next: (res) => (this.rates = this.mapper.mapRateOptions(res)),
      error: () => (this.rates = []),
    });

    this.api.getParkingGateGroupDropdown(this.siteId).subscribe({
      next: (res) => (this.gateGroups = this.mapper.mapIdNameList(res)),
      error: () => (this.gateGroups = []),
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
